'use client';

import { useCallback, useEffect, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import type { LucideIcon } from 'lucide-react';
import { Bot, FileText, Film, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { cachedJson, peekJson } from './discover-data';

/**
 * The first-run band: four cards, no band label, each one building a
 * finished working screen in a single click. Card shape teaches the model —
 * a starter is SCREEN-shaped because clicking it produces a screen.
 *
 * Spawn-then-tile depends on two things this build put in place: the card
 * actions now RETURN the created item (they were fire-and-forget, so nothing
 * could tile what it could not name), and `addItem` resolves only after the
 * resolve-ref round trip, so the min/max sizes the tiler reads are present
 * before the layout event dispatches.
 *
 * Three starters fill the screen (`layout: 'full'` — the tiling path, never
 * the fullscreen takeover, which would hide the rail one click after a new
 * user first saw it). The agent card is the single tiled pair: an agent
 * alone is a chat box with nothing to act on; an agent beside a document is
 * the product's thesis.
 */

type StarterWidget = {
  contentId: string;
  title: string | null;
  metadata: Record<string, unknown> | null;
  postId: string | null;
};

type StartersPayload = {
  starters: { key: string; label: string; widgetType: string; widget: StarterWidget }[];
  agent: { id: string; username: string; displayName: string | null; avatarUrl: string | null } | null;
  feedback: StarterWidget | null;
};

const STARTER_ICON: Record<string, LucideIcon> = {
  docs: FileText,
  whiteboard: PenLine,
  video: Film,
};

const STARTER_APP_LINE: Record<string, string> = {
  docs: 'Docs',
  whiteboard: 'Whiteboard',
  video: 'Video Studio',
};

/** Chip icon tones — the Recent row's TONE_CLASS palette, one hue per verb. */
const STARTER_TONE: Record<string, string> = {
  docs: 'bg-sky-500/10 text-sky-600 dark:bg-sky-400/15 dark:text-sky-400',
  whiteboard: 'bg-amber-500/10 text-amber-600 dark:bg-amber-400/15 dark:text-amber-400',
  video: 'bg-violet-500/10 text-violet-600 dark:bg-violet-400/15 dark:text-violet-400',
  agent: 'bg-emerald-500/10 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-400',
};

const STARTERS_URL = '/api/workspaces/starters';

export function useStarters(): StartersPayload | null {
  // Rides the shared Discover cache (warmed by the shell prefetch), so the
  // band paints with the surface instead of popping in after its own fetch.
  const [data, setData] = useState<StartersPayload | null>(() =>
    peekJson<StartersPayload>(STARTERS_URL),
  );
  useEffect(() => {
    let alive = true;
    void cachedJson<StartersPayload>(STARTERS_URL, 5 * 60_000).then((d) => {
      if (alive && d) setData(d);
    });
    return () => {
      alive = false;
    };
  }, []);
  return data;
}

/** Two paint frames — the layout handler reads the canvas's items ref, which
 * updates on the render AFTER the store set; dispatching in the same tick
 * would tile against a snapshot that does not contain the new item yet. */
const afterPaint = () =>
  new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );

const dispatchLayout = (itemId: string, layout: 'full' | 'left' | 'right') =>
  window.dispatchEvent(
    new CustomEvent('ws:screen', { detail: { kind: 'layout', itemId, layout } }),
  );

export function StarterBand({ className }: { className?: string }) {
  const t = useT();
  const data = useStarters();
  const cardActions = useWorkspaceCardActions();
  const [busy, setBusy] = useState<string | null>(null);

  const launchSingle = useCallback(
    async (key: string, widget: StarterWidget) => {
      if (!cardActions || busy) return;
      setBusy(key);
      try {
        const item = await cardActions.addWidget({
          contentId: widget.contentId,
          title: widget.title,
          metadata: widget.metadata,
          sourceWidgetPostId: widget.postId,
          // These starters compose their OWN layout below (`dispatchLayout`),
          // so they must land on the screen being composed — never on a fresh
          // one, which would split the pair across two screens.
          screen: 'current',
        });
        if (item?.id) {
          await afterPaint();
          dispatchLayout(item.id, 'full');
        }
      } finally {
        setBusy(null);
      }
    },
    [cardActions, busy],
  );

  const launchAgentPair = useCallback(async () => {
    if (!cardActions || !data?.agent || busy) return;
    setBusy('agent');
    try {
      const docs = data.starters.find((s) => s.key === 'docs')?.widget ?? null;
      // Document first, agent second — the camera follows the last spawn, and
      // focus should land where the user can type.
      const docItem = docs
        ? await cardActions.addWidget({
            contentId: docs.contentId,
            title: docs.title,
            metadata: docs.metadata,
            sourceWidgetPostId: docs.postId,
            screen: 'current',
          })
        : null;
      const agentItem = await cardActions.addAgent({
        agentId: data.agent.id,
        username: data.agent.username,
        displayName: data.agent.displayName,
        avatarUrl: data.agent.avatarUrl,
        screen: 'current',
      });
      await afterPaint();
      if (agentItem?.id && docItem?.id) {
        dispatchLayout(agentItem.id, 'left');
        dispatchLayout(docItem.id, 'right');
      } else if (agentItem?.id) {
        dispatchLayout(agentItem.id, 'full');
      }
    } finally {
      setBusy(null);
    }
  }, [cardActions, data, busy]);

  if (!data || data.starters.length === 0) return null;

  // One scrolling line of icon+name chips — the EXACT anatomy of the Recent
  // row this band alternates with (first run shows starters, every later
  // visit shows Recent in the same slot), so the surface doesn't change
  // shape between visit one and visit two.
  const chipCls =
    'inline-flex shrink-0 items-center gap-2 rounded-lg border border-border/60 bg-background py-1.5 pl-1.5 pr-3 text-left transition-colors hover:bg-accent/40 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60';
  const iconCls = 'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md';
  const labelCls = 'max-w-[11rem] truncate text-xs font-medium text-foreground';

  return (
    <div
      className={cn(
        'flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [mask-image:linear-gradient(to_right,black,black_calc(100%_-_2.5rem),transparent)]',
        className,
      )}
      style={{ scrollbarWidth: 'none' }}
    >
      {data.starters.map((s) => {
        const Icon = STARTER_ICON[s.key] ?? FileText;
        return (
          <button
            key={s.key}
            type="button"
            disabled={busy !== null}
            onClick={() => void launchSingle(s.key, s.widget)}
            title={`${s.label} · ${STARTER_APP_LINE[s.key] ?? s.widgetType}`}
            className={chipCls}
          >
            <span className={cn(iconCls, STARTER_TONE[s.key] ?? STARTER_TONE.docs)}>
              <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
            </span>
            <span className={labelCls}>{s.label}</span>
          </button>
        );
      })}
      {data.agent && (
        <button
          type="button"
          disabled={busy !== null}
          onClick={() => void launchAgentPair()}
          title={t('starterBand.workWithAgentTitle')}
          className={chipCls}
        >
          <span className={cn(iconCls, STARTER_TONE.agent)}>
            <Bot className="h-3.5 w-3.5" strokeWidth={1.8} />
          </span>
          <span className={labelCls}>{t('starterBand.workWithAgent')}</span>
        </button>
      )}
    </div>
  );
}
